import { useState, type FormEvent } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { INVITE_MODAL_LABELS as LABELS, ROLE_LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';
import type { MemberRole } from '../../../../types/roles';

interface InviteUserModalProps {
  onClose: () => void;
  onInvite: (email: string, firstName: string, lastName: string, role: MemberRole) => Promise<'ok' | 'error'>;
}

export default function InviteUserModal({ onClose, onInvite }: InviteUserModalProps) {
  useModalHistory(onClose);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('full_access');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ firstName: '', lastName: '', email: '' });
  const [serverError, setServerError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const fieldErrors = {
      firstName: !firstName.trim() ? LABELS.firstNameRequired : '',
      lastName: !lastName.trim() ? LABELS.lastNameRequired : '',
      email: !email.trim() ? LABELS.emailRequired : '',
    };
    if (Object.values(fieldErrors).some(Boolean)) {
      setErrors(fieldErrors);
      return;
    }
    setErrors({ firstName: '', lastName: '', email: '' });
    setServerError('');
    setLoading(true);
    const result = await onInvite(email, firstName, lastName, role);
    setLoading(false);
    if (result === 'ok') {
      onClose();
    } else {
      setServerError(LABELS.inviteError);
    }
  };

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth title={LABELS.title} confirmLabel={LABELS.send} confirmFormId="invite-user-form" confirmDisabled={loading} confirmLoading={loading} testId="invite-user-modal">
      <form id="invite-user-form" onSubmit={handleSubmit} noValidate>

        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.firstNameLabel}
            value={firstName}
            onChange={(e) => { setFirstName(e.target.value); if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: '' })); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!errors.firstName}
            helperText={errors.firstName || undefined}
          />
          <TextField
            label={LABELS.lastNameLabel}
            value={lastName}
            onChange={(e) => { setLastName(e.target.value); if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: '' })); }}
            required
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName || undefined}
          />
          <TextField
            type="email"
            label={LABELS.emailLabel}
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errors.email || serverError) { setErrors((prev) => ({ ...prev, email: '' })); setServerError(''); } }}
            required
            fullWidth
            error={!!errors.email || !!serverError}
            helperText={errors.email || undefined}
          />
          <FormControl fullWidth>
            <InputLabel>{LABELS.roleLabel}</InputLabel>
            <Select
              label={LABELS.roleLabel}
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
              inputProps={{ 'aria-label': LABELS.roleLabel }}
            >
              {(Object.entries(ROLE_LABELS) as [MemberRole, string][]).map(([key, label]) => (
                <MenuItem key={key} value={key} data-testid={`option-role-${key}`}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {serverError && (
            <p role="alert" className="text-sm text-red-700 mt-0">
              {serverError}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={loading} data-testid="save-button">
            {LABELS.send}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
